/**
 * SettingsPanel — the Settings overview screen. It edits builder-level metadata only
 * (never visual components):
 *   - Website: SEO title (also the site name) + description (top-level schema.seo).
 *   - Two navigable groups: Location details and Social links (drill in via Back nav).
 *   - Read-only builder facts from the persisted record.
 *
 * Business contact/location/social are stored as builder metadata (schema.businessInfo),
 * consistent with V1's "no content editing" scope.
 */
import { MapPin, Share2 } from 'lucide-react'
import { useBuilderEditor } from './editorContext'
import { Fieldset, TextField, TextArea, ReadOnlyRow, MenuList, MenuItem } from './controls'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

export function SettingsPanel({
  onOpenLocation,
  onOpenSocial,
}: {
  onOpenLocation: () => void
  onOpenSocial: () => void
}) {
  const e = useBuilderEditor()
  const { builder, schema } = e
  const providerLabel = builder.provider === 'mock' ? 'Mock' : 'Live'

  return (
    <div>
      <Fieldset title="Website">
        <TextField label="Site title" value={schema.seo.title} onChange={e.setSeoTitle} placeholder="Your business name" />
        <TextArea label="SEO description" value={schema.seo.description} onChange={e.setSeoDescription} rows={3} maxLength={200} />
      </Fieldset>

      <MenuList>
        <MenuItem label="Location details" description="Email, phone, address" icon={MapPin} onClick={onOpenLocation} />
        <MenuItem label="Social links" description="Facebook, Instagram, X…" icon={Share2} onClick={onOpenSocial} />
      </MenuList>

      <Fieldset title="Builder">
        <div className="flex flex-col divide-y divide-gray-50">
          <ReadOnlyRow label="Status" value={builder.status} />
          <ReadOnlyRow label="Provider" value={providerLabel} />
          <ReadOnlyRow label="Language" value={builder.locale.toUpperCase()} />
          <ReadOnlyRow label="Design system" value={schema.theme.designSystem} />
          <ReadOnlyRow label="Generated" value={formatDate(builder.createdAt)} />
          <ReadOnlyRow label="Last updated" value={formatDate(e.lastSavedAt)} />
          <ReadOnlyRow label="Business ID" value={builder.businessId} />
          <ReadOnlyRow label="Builder ID" value={builder.id} />
        </div>
      </Fieldset>
    </div>
  )
}
